/**
 * InlineTable Component
 * 
 * A structured table component for displaying data in rows and cells.
 * Cloned from Carbon's StructuredList with adaptations for the UI package.
 * 
 * Features:
 * - Selectable rows with checkbox indicators
 * - Condensed and flush variants
 * - Header and body sections
 * - Focus management and keyboard navigation
 */

import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  type HTMLAttributes,
  type ReactNode,
  type KeyboardEvent,
  type ChangeEvent,
  type MouseEvent,
} from 'react';
import { Checkbox } from '../../interactives/Checkbox';
import { Tag, seg } from '../../tags/Tag';
import { Scrollbar } from '../../scrollbar/Scrollbar';
import { StateCell, ArrayCell, TextCell, LinkCell, ActionCell, EnumCell, NumberCell, SensitiveCell, DateCell, IconCell } from '../../cells';
import { useTableScroll } from '../shared/useTableScroll';
import type { PrimaryTableColumn } from '../PrimaryTable/types';

type DivAttrs = HTMLAttributes<HTMLDivElement>;

type GridSelectedRowState = null | string;
type GridSelectedRowSetter = null | ((value: GridSelectedRowState) => void);
const GridSelectedRowStateContext =
  React.createContext<GridSelectedRowState>(null);
const GridSelectedRowDispatchContext =
  React.createContext<GridSelectedRowSetter>(null);

export interface InlineTableWrapperProps extends DivAttrs {
  /**
   * Specify a label to be read by screen readers on the container node
   */
  'aria-label'?: string;

  /**
   * Provide the contents of your InlineTableWrapper
   */
  children?: ReactNode;

  /**
   * Specify an optional className to be applied to the container node
   */
  className?: string;

  /**
   * Visual variant for the table
   * - 'minimal': Clean lines only, no backgrounds, no outer border (default for docs)
   * - 'bordered': Tile-style with border, rounded corners, header background
   */
  variant?: 'minimal' | 'bordered';

  /**
   * Specify if inline table is condensed, default is false
   */
  isCondensed?: boolean;

  /**
   * Specify if inline table is flush, default is false
   */
  isFlush?: boolean;

  /**
   * Specify whether your InlineTableWrapper should have selections
   */
  selection?: boolean;

  /**
   * Number of rows to show initially (enables expansion)
   */
  initialRows?: number;

  /**
   * Number of additional rows to show per "Show More" click
   */
  expandBy?: number;

  /**
   * Maximum total rows to show (number or "all")
   */
  maxRows?: number | 'all';

  /**
   * Data-driven mode: Columns configuration (like PrimaryTable)
   */
  columns?: PrimaryTableColumn[];

  /**
   * Data-driven mode: Rows data (like PrimaryTable)
   */
  rows?: Record<string, any>[];

  /**
   * Enable horizontal scrolling with custom scrollbar
   */
  enableScroll?: boolean;
}

export function InlineTableWrapper(props: InlineTableWrapperProps) {
  const {
    children,
    selection,
    className,
    variant = 'minimal',
    ['aria-label']: ariaLabel = 'Inline table section',
    isCondensed,
    isFlush,
    initialRows,
    expandBy = 10,
    maxRows = 'all',
    columns,
    rows,
    enableScroll = false,
    ...other
  } = props;

  const classes = [
    'core-inline-table',
    variant && `core-inline-table--${variant}`,
    selection && 'core-inline-table--selection',
    isCondensed && 'core-inline-table--condensed',
    isFlush && !selection && 'core-inline-table--flush',
    className,
  ].filter(Boolean).join(' ');

  const [selectedRow, setSelectedRow] =
    React.useState<GridSelectedRowState>(null);
  
  const [visibleRows, setVisibleRows] = React.useState<number>(
    initialRows || Infinity
  );
  
  const [hasExpanded, setHasExpanded] = React.useState<boolean>(false);
  
  // Use shared scroll hook
  const { scrollContainerRef, scrollElement, isScrollable } = useTableScroll({
    enabled: enableScroll,
    debounceMs: 100,
    initialDelayMs: 10
  });
  
  // Cell rendering helper function (matches PrimaryTable)
  const renderCellContent = useCallback((
    column: PrimaryTableColumn,
    value: any,
    row: Record<string, any>
  ): React.ReactNode => {
    // Custom renderCell takes precedence
    if (column.renderCell) {
      return column.renderCell(value, row);
    }
    
    // Type-based rendering
    switch (column.type) {
      case 'state':
        return <StateCell value={value} config={column.stateConfig} row={row} rowIndex={0} />;
      case 'indicator':
        return <StateCell value={value} config={column.stateConfig} row={row} rowIndex={0} />;
      case 'array':
        return <ArrayCell value={value} config={column.arrayConfig} row={row} />;
      case 'link':
        return <LinkCell value={value} config={column.linkConfig} row={row} />;
      case 'enum':
        return <EnumCell value={value} config={column.enumConfig} row={row} />;
      case 'integer':
      case 'number':
        return <NumberCell value={value} config={column.numberConfig || column.integerConfig as any} row={row} />;
      case 'sensitive':
        return <SensitiveCell value={value} config={column.sensitiveConfig} row={row} />;
      case 'action':
        return <ActionCell value={value} config={column.actionConfig} row={row} rowIndex={0} />;
      case 'icon':
        return <IconCell value={value} config={column.iconConfig} row={row} />;
      case 'datetime':
      case 'date':
        return <DateCell value={value} config={column.dateConfig} row={row} />;
      case 'text':
      case 'custom':
      default:
        return <TextCell value={value} config={column.textConfig} />;
    }
  }, []);

  // Process children to handle expansion
  const processedChildren = React.useMemo(() => {
    if (!initialRows) return children;

    const childArray = React.Children.toArray(children);
    let header: ReactNode = null;
    let body: ReactNode = null;

    // Find header and body
    childArray.forEach((child) => {
      if (React.isValidElement(child)) {
        const childType = (child.type as any);
        const displayName = childType?.displayName || childType?.name;
        
        if (displayName === 'InlineTableHead' || childType === InlineTableHead) {
          header = child;
        } else if (displayName === 'InlineTableBody' || childType === InlineTableBody) {
          body = child;
        }
      }
    });

    // If we have a body with children, limit the rows
    if (body && React.isValidElement(body)) {
      const bodyProps = body.props as any;
      if (bodyProps && bodyProps.children) {
        const bodyRows = React.Children.toArray(bodyProps.children);
        const limitedRows = bodyRows.slice(0, visibleRows);
        
        const newBody = React.cloneElement(body as React.ReactElement<any>, {
          ...bodyProps,
          children: limitedRows,
        });

        return (
          <>
            {header}
            {newBody}
          </>
        );
      }
    }

    return children;
  }, [children, initialRows, visibleRows]);

  // Calculate if we can show more
  const canShowMore = React.useMemo(() => {
    if (!initialRows) return false;

    const childArray = React.Children.toArray(children);
    let totalRows = 0;

    childArray.forEach((child) => {
      if (React.isValidElement(child)) {
        const childType = (child.type as any);
        const displayName = childType?.displayName || childType?.name;
        
        if (displayName === 'InlineTableBody' || childType === InlineTableBody) {
          const bodyProps = (child.props as any);
          if (bodyProps && bodyProps.children) {
            totalRows = React.Children.count(bodyProps.children);
          }
        }
      }
    });

    const effectiveMax = maxRows === 'all' ? totalRows : maxRows;
    return visibleRows < totalRows && visibleRows < effectiveMax;
  }, [children, initialRows, visibleRows, maxRows]);

  const handleShowMore = () => {
    const childArray = React.Children.toArray(children);
    let totalRows = 0;

    childArray.forEach((child) => {
      if (React.isValidElement(child)) {
        const childType = (child.type as any);
        const displayName = childType?.displayName || childType?.name;
        
        if (displayName === 'InlineTableBody' || childType === InlineTableBody) {
          const bodyProps = (child.props as any);
          if (bodyProps && bodyProps.children) {
            totalRows = React.Children.count(bodyProps.children);
          }
        }
      }
    });

    const effectiveMax = maxRows === 'all' ? totalRows : maxRows;
    const nextVisible = Math.min(visibleRows + expandBy, effectiveMax, totalRows);
    setVisibleRows(nextVisible);
    setHasExpanded(true);
  };

  const handleShowLess = () => {
    setVisibleRows(initialRows || 10);
    setHasExpanded(false);
  };

  // Check if we can show less
  const canShowLess = hasExpanded && initialRows && visibleRows > initialRows;
  
  // Data-driven mode: Generate table from columns and rows
  const dataContent = useMemo(() => {
    if (!columns || !rows) return null;
    
    const displayRows = initialRows ? rows.slice(0, visibleRows) : rows;
    
    return (
      <>
        <InlineTableHead>
          <InlineTableRow head>
            {columns.map((column) => (
              <InlineTableCell key={column.key} head style={{ width: column.width, minWidth: column.width }}>
                {column.header}
              </InlineTableCell>
            ))}
          </InlineTableRow>
        </InlineTableHead>
        <InlineTableBody>
          {displayRows.map((row, rowIndex) => (
            <InlineTableRow key={rowIndex}>
              {columns.map((column) => (
                <InlineTableCell key={column.key} style={{ width: column.width, minWidth: column.width }}>
                  {renderCellContent(column, row[column.key], row)}
                </InlineTableCell>
              ))}
            </InlineTableRow>
          ))}
        </InlineTableBody>
      </>
    );
  }, [columns, rows, visibleRows, initialRows, renderCellContent]);
  
  // Determine content to render
  const content = columns && rows ? dataContent : processedChildren;

  return (
    <>
      <GridSelectedRowStateContext.Provider value={selectedRow}>
        <GridSelectedRowDispatchContext.Provider value={setSelectedRow}>
          {enableScroll ? (
            <div style={{ width: '100%' }}>
              <div className={`inline-table-container ${variant === 'bordered' ? 'inline-table-container--bordered' : ''}`}>
                <div
                  ref={scrollContainerRef}
                  style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                  }}
                  className="inline-table-scroll-container"
                >
                  <style>{`
                    .inline-table-scroll-container::-webkit-scrollbar {
                      display: none;
                    }
                  `}</style>
                  <div
                    role="table"
                    className={classes}
                    {...other}
                    aria-label={ariaLabel}>
                    {content}
                  </div>
                </div>
              </div>
              {isScrollable && (
                <div style={{ paddingTop: '16px', paddingBottom: '16px' }}>
                  <Scrollbar 
                    scrollElement={scrollElement}
                    className="inline-table-custom-scrollbar"
                  />
                </div>
              )}
            </div>
          ) : variant === 'bordered' ? (
            <div className="inline-table-container inline-table-container--bordered">
              <div
                role="table"
                className={classes}
                {...other}
                aria-label={ariaLabel}>
                {content}
              </div>
            </div>
          ) : (
            <div
              role="table"
              className={classes}
              {...other}
              aria-label={ariaLabel}>
              {content}
            </div>
          )}
        </GridSelectedRowDispatchContext.Provider>
      </GridSelectedRowStateContext.Provider>
      
      {(canShowMore || canShowLess) && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '0.5rem',
          marginTop: '1rem' 
        }}>
          {canShowMore && (
            <div style={{
              animation: 'fadeIn 200ms ease-in',
              opacity: canShowMore ? 1 : 0,
              transition: 'opacity 200ms ease-in-out'
            }}>
              <Tag
                segments={[seg.text(canShowLess ? "More" : "Show More")]}
                size="lg"
                border="subtle"
                onClick={handleShowMore}
              />
            </div>
          )}
          
          {canShowLess && (
            <div style={{
              animation: 'fadeIn 200ms ease-in',
              opacity: canShowLess ? 1 : 0,
              transition: 'opacity 200ms ease-in-out'
            }}>
              <Tag
                segments={[seg.text(canShowMore ? "Less" : "Show Less")]}
                size="lg"
                border="subtle"
                onClick={handleShowLess}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export interface InlineTableHeadProps extends DivAttrs {
  /**
   * Provide the contents of your InlineTableHead
   */
  children?: ReactNode;

  /**
   * Specify an optional className to be applied to the node
   */
  className?: string;
}

export function InlineTableHead(props: InlineTableHeadProps) {
  const { children, className, ...other } = props;
  const classes = ['core-inline-table-thead', className].filter(Boolean).join(' ');
  
  return (
    <div role="rowgroup" className={classes} {...other}>
      {children}
    </div>
  );
}
InlineTableHead.displayName = 'InlineTableHead';

export interface InlineTableBodyProps extends DivAttrs {
  /**
   * Provide the contents of your InlineTableBody
   */
  children?: ReactNode;

  /**
   * Specify an optional className to be applied to the container node
   */
  className?: string;

  head?: boolean;

  /**
   * Provide a handler that is invoked on the key down event for the control
   */
  onKeyDown?(event: KeyboardEvent): void;
}

export function InlineTableBody(props: InlineTableBodyProps) {
  const { children, className, ...other } = props;
  const classes = ['core-inline-table-tbody', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="rowgroup" {...other}>
      {children}
    </div>
  );
}
InlineTableBody.displayName = 'InlineTableBody';

const GridRowContext = React.createContext<null | { id: string }>(null);

export interface InlineTableRowProps extends DivAttrs {
  /**
   * Provide the contents of your InlineTableRow
   */
  children?: ReactNode;

  /**
   * Specify an optional className to be applied to the container node
   */
  className?: string;

  /**
   * Specify whether your InlineTableRow should be used as a header row
   */
  head?: boolean;

  /**
   * Provide a handler that is invoked on the click
   */
  onClick?(event: MouseEvent): void;

  /**
   * Provide a handler that is invoked on the key down event for the control
   */
  onKeyDown?(event: KeyboardEvent): void;

  /**
   * Mark if this row should be selectable
   */
  selection?: boolean;
}

export function InlineTableRow(props: InlineTableRowProps) {
  const { onKeyDown, children, className, head, onClick, selection, ...other } =
    props;
  const [hasFocusWithin, setHasFocusWithin] = useState(false);
  const idRef = useRef<string>(`inline-table-row-${Math.random().toString(36).substr(2, 9)}`);
  const id = idRef.current;
  const selectedRow = React.useContext(GridSelectedRowStateContext);
  const setSelectedRow = React.useContext(GridSelectedRowDispatchContext);
  const value = { id };
  const itemRef = useRef<HTMLDivElement | null>(null);

  const classes = [
    'core-inline-table-row',
    head && 'core-inline-table-row--header-row',
    ((hasFocusWithin && !selection) ||
      (hasFocusWithin && selection && (selectedRow === id || selectedRow === null))) &&
      'core-inline-table-row--focused-within',
    selectedRow === id && 'core-inline-table-row--selected',
    className,
  ].filter(Boolean).join(' ');

  const handleClickOutside = (event: MouseEvent | TouchEvent) => {
    if (itemRef.current && !itemRef.current.contains(event.target as Node)) {
      setHasFocusWithin(false);
    }
  };

  React.useEffect(() => {
    if (hasFocusWithin) {
      document.addEventListener('mousedown', handleClickOutside as any);
      document.addEventListener('touchstart', handleClickOutside as any);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as any);
      document.removeEventListener('touchstart', handleClickOutside as any);
    };
  }, [hasFocusWithin]);

  return head ? (
    <div role="row" {...other} className={classes}>
      {selection && <InlineTableCell head></InlineTableCell>}
      {children}
    </div>
  ) : (
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      {...other}
      role="row"
      className={classes}
      ref={itemRef}
      onClick={(event) => {
        setSelectedRow?.(id);
        onClick && onClick(event);
        if (selection) {
          setHasFocusWithin(true);
        }
      }}
      onFocus={() => {
        setHasFocusWithin(true);
      }}
      onBlur={() => {
        setHasFocusWithin(false);
      }}
      onKeyDown={onKeyDown}>
      <GridRowContext.Provider value={value}>
        {selection && (
          <InlineTableCell>
            <div className="core-inline-table__checkbox">
              <Checkbox
                id={`checkbox-${id}`}
                checked={selectedRow === id}
                onChange={() => setSelectedRow?.(id)}
                ariaLabel={`Select row ${id}`}
              />
            </div>
          </InlineTableCell>
        )}
        {children}
      </GridRowContext.Provider>
    </div>
  );
}

export interface InlineTableInputProps extends DivAttrs {
  /**
   * Specify an optional className to be applied to the input
   */
  className?: string;

  /**
   * Specify a custom `id` for the input
   */
  id?: string;

  /**
   * Provide a `name` for the input
   */
  name?: string;

  /**
   * Provide an optional hook that is called each time the input is updated
   */
  onChange?(event: ChangeEvent<HTMLInputElement>): void;

  /**
   * Provide a `title` for the input
   */
  title?: string;
}

export function InlineTableInput(props: InlineTableInputProps) {
  const defaultIdRef = useRef<string>(`inline-table-input-${Math.random().toString(36).substr(2, 9)}`);
  const defaultId = defaultIdRef.current;
  const {
    className,
    name = `inline-table-input-${defaultId}`,
    title,
    id,
    onChange,
    ...other
  } = props;
  
  const classes = [
    'core-inline-table-input',
    'core-visually-hidden',
    className,
  ].filter(Boolean).join(' ');
  
  const row = React.useContext(GridRowContext);
  const selectedRow = React.useContext(GridSelectedRowStateContext);
  const setSelectedRow = React.useContext(GridSelectedRowDispatchContext);

  return (
    <input
      {...other}
      type="radio"
      tabIndex={0}
      checked={!!row && row.id === selectedRow}
      value={row?.id ?? ''}
      onChange={(event) => {
        setSelectedRow?.(event.target.value);
        onChange && onChange(event);
      }}
      id={id ?? defaultId}
      className={classes}
      name={name}
      title={title}
    />
  );
}

export interface InlineTableCellProps extends DivAttrs {
  /**
   * Provide the contents of your InlineTableCell
   */
  children?: ReactNode;

  /**
   * Specify an optional className to be applied to the container node
   */
  className?: string;

  /**
   * Specify whether your InlineTableCell should be used as a header cell
   */
  head?: boolean;

  /**
   * Specify whether your InlineTableCell should have text wrapping
   */
  noWrap?: boolean;
}

export function InlineTableCell(props: InlineTableCellProps) {
  const { children, className, head, noWrap, ...other } = props;
  
  const classes = [
    head ? 'core-inline-table-th' : 'core-inline-table-td',
    noWrap && 'core-inline-table-content--nowrap',
    className,
  ].filter(Boolean).join(' ');

  if (head) {
    return (
      <div className={classes} role="columnheader" {...other}>
        {children}
      </div>
    );
  }

  return (
    <div className={classes} role="cell" {...other}>
      {children}
    </div>
  );
}
